import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

interface PinAuditLog {
  id: string;
  guestId: string;
  guestEmail: string;
  action: 'PIN_GENERATED' | 'PIN_CHANGED' | 'PIN_LOGIN_SUCCESS' | 'PIN_LOGIN_FAILED' | 'ACCOUNT_LOCKED' | 'ACCOUNT_UNLOCKED';
  adminId?: string;
  ipAddress?: string;
  userAgent?: string;
  details?: {
    oldPinHash?: string;
    newPinHash?: string;
    failedAttempts?: number;
    lockDuration?: number;
    changedBy?: string;
    generatedBy?: string;
    unlockedBy?: string;
    reason?: string;
    [key: string]: any;
  };
  createdAt: string;
}

interface PinAuditResponse {
  success: boolean;
  data: {
    logs: PinAuditLog[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    summary?: Record<string, number>;
    statistics?: {
      totalEvents: number;
      uniqueGuests: number;
      uniqueAdmins: number;
      timeframe: string;
    };
  };
}

interface SecurityEventsResponse {
  success: boolean;
  data: {
    events: PinAuditLog[];
    summary: {
      totalEvents: number;
      timeframe: string;
      severity: string;
      PIN_LOGIN_FAILED?: number;
      ACCOUNT_LOCKED?: number;
      ACCOUNT_UNLOCKED?: number;
    };
    topFailedIPs: Array<{ ip: string; count: number }>;
  };
}

interface StatisticsResponse {
  success: boolean;
  data: {
    timeframe: string;
    actionCounts: Record<string, number>;
    summary: {
      totalEvents: number;
      totalLogins: number;
      successfulLogins: number;
      failedLogins: number;
      successRate: number;
      accountsLocked: number;
      uniqueGuestsWithActivity: number;
    };
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`audit-tabpanel-${index}`}
      aria-labelledby={`audit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const PinAuditLogViewer: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [auditLogs, setAuditLogs] = useState<PinAuditLog[]>([]);
  const [securityEvents, setSecurityEvents] = useState<PinAuditLog[]>([]);
  const [statistics, setStatistics] = useState<StatisticsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [actionFilter, setActionFilter] = useState('');
  const [guestFilter, setGuestFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [hoursFilter, setHoursFilter] = useState(24);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState(7);

  // Pagination
  const [totalPages, setTotalPages] = useState(1);
  const [topFailedIPs, setTopFailedIPs] = useState<Array<{ ip: string; count: number }>>([]);

  useEffect(() => {
    loadAuditData();
  }, [currentTab, page, limit, actionFilter, guestFilter, adminFilter, hoursFilter, severityFilter, daysFilter]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { ApiService } = await import('../services/api');

      if (currentTab === 0) {
        // Load general audit logs
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          ...(actionFilter && { action: actionFilter }),
          ...(guestFilter && { guestId: guestFilter }),
          ...(adminFilter && { adminId: adminFilter }),
          ...(hoursFilter && { hours: hoursFilter.toString() })
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${API_BASE_URL}/pin-audit?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result: PinAuditResponse = await response.json();
          if (result.success) {
            setAuditLogs(result.data.logs);
            if (result.data.pagination) {
              setTotalPages(result.data.pagination.totalPages);
            }
          }
        }
      } else if (currentTab === 1) {
        // Load security events
        const params = new URLSearchParams({
          hours: hoursFilter.toString(),
          severity: severityFilter
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${API_BASE_URL}/pin-audit/security-events?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result: SecurityEventsResponse = await response.json();
          if (result.success) {
            setSecurityEvents(result.data.events);
            setTopFailedIPs(result.data.topFailedIPs);
          }
        }
      } else if (currentTab === 2) {
        // Load statistics
        const params = new URLSearchParams({
          days: daysFilter.toString()
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${API_BASE_URL}/pin-audit/statistics?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result: StatisticsResponse = await response.json();
          if (result.success) {
            setStatistics(result.data);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'PIN_GENERATED':
        return <AdminIcon color="primary" />;
      case 'PIN_CHANGED':
        return <PersonIcon color="info" />;
      case 'PIN_LOGIN_SUCCESS':
        return <SuccessIcon color="success" />;
      case 'PIN_LOGIN_FAILED':
        return <WarningIcon color="warning" />;
      case 'ACCOUNT_LOCKED':
        return <ErrorIcon color="error" />;
      case 'ACCOUNT_UNLOCKED':
        return <SecurityIcon color="success" />;
      default:
        return <TimelineIcon />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'PIN_GENERATED':
        return 'primary';
      case 'PIN_CHANGED':
        return 'info';
      case 'PIN_LOGIN_SUCCESS':
        return 'success';
      case 'PIN_LOGIN_FAILED':
        return 'warning';
      case 'ACCOUNT_LOCKED':
        return 'error';
      case 'ACCOUNT_UNLOCKED':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDetails = (details: any) => {
    if (!details) return 'N/A';
    
    const formatted = Object.entries(details)
      .filter(([key, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    
    return formatted || 'N/A';
  };

  const exportAuditLogs = async () => {
    try {
      const params = new URLSearchParams({
        ...(actionFilter && { action: actionFilter }),
        ...(guestFilter && { guestId: guestFilter }),
        ...(adminFilter && { adminId: adminFilter }),
        hours: hoursFilter.toString(),
        limit: '1000' // Export more records
      });

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/pin-audit?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result: PinAuditResponse = await response.json();
        if (result.success) {
          // Convert to CSV
          const csvContent = [
            ['Timestamp', 'Guest Email', 'Action', 'Admin ID', 'IP Address', 'Details'].join(','),
            ...result.data.logs.map(log => [
              new Date(log.createdAt).toISOString(),
              log.guestEmail,
              log.action,
              log.adminId || '',
              log.ipAddress || '',
              formatDetails(log.details).replace(/,/g, ';') // Replace commas in details
            ].join(','))
          ].join('\n');

          // Download CSV
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pin-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      setError('Failed to export audit logs');
    }
  };

  const clearFilters = () => {
    setActionFilter('');
    setGuestFilter('');
    setAdminFilter('');
    setHoursFilter(24);
    setSeverityFilter('all');
    setDaysFilter(7);
    setPage(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" gutterBottom>
          PIN Audit Log Viewer
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadAuditData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Logs">
            <IconButton onClick={exportAuditLogs} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="All Audit Logs" />
            <Tab label="Security Events" />
            <Tab label="Statistics" />
          </Tabs>
        </Box>

        {/* All Audit Logs Tab */}
        <TabPanel value={currentTab} index={0}>
          {/* Filters */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <FilterIcon sx={{ mr: 1 }} />
                <Typography>Filters</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={actionFilter}
                      label="Action"
                      onChange={(e) => setActionFilter(e.target.value)}
                    >
                      <MenuItem value="">All Actions</MenuItem>
                      <MenuItem value="PIN_GENERATED">PIN Generated</MenuItem>
                      <MenuItem value="PIN_CHANGED">PIN Changed</MenuItem>
                      <MenuItem value="PIN_LOGIN_SUCCESS">Login Success</MenuItem>
                      <MenuItem value="PIN_LOGIN_FAILED">Login Failed</MenuItem>
                      <MenuItem value="ACCOUNT_LOCKED">Account Locked</MenuItem>
                      <MenuItem value="ACCOUNT_UNLOCKED">Account Unlocked</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Guest Email"
                    value={guestFilter}
                    onChange={(e) => setGuestFilter(e.target.value)}
                    placeholder="Filter by guest email"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Admin ID"
                    value={adminFilter}
                    onChange={(e) => setAdminFilter(e.target.value)}
                    placeholder="Filter by admin ID"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={hoursFilter}
                      label="Time Range"
                      onChange={(e) => setHoursFilter(Number(e.target.value))}
                    >
                      <MenuItem value={1}>Last Hour</MenuItem>
                      <MenuItem value={6}>Last 6 Hours</MenuItem>
                      <MenuItem value={24}>Last 24 Hours</MenuItem>
                      <MenuItem value={72}>Last 3 Days</MenuItem>
                      <MenuItem value={168}>Last Week</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button onClick={clearFilters} variant="outlined" size="small">
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Audit Logs Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getActionIcon(log.action)}
                        <Chip
                          label={log.action.replace(/_/g, ' ')}
                          size="small"
                          color={getActionColor(log.action) as any}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{log.guestEmail}</TableCell>
                    <TableCell>{log.adminId || '-'}</TableCell>
                    <TableCell>{log.ipAddress || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatDetails(log.details)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        </TabPanel>

        {/* Security Events Tab */}
        <TabPanel value={currentTab} index={1}>
          {/* Security Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={hoursFilter}
                  label="Time Range"
                  onChange={(e) => setHoursFilter(Number(e.target.value))}
                >
                  <MenuItem value={1}>Last Hour</MenuItem>
                  <MenuItem value={6}>Last 6 Hours</MenuItem>
                  <MenuItem value={24}>Last 24 Hours</MenuItem>
                  <MenuItem value={72}>Last 3 Days</MenuItem>
                  <MenuItem value={168}>Last Week</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  label="Severity"
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Events</MenuItem>
                  <MenuItem value="high">High Severity</MenuItem>
                  <MenuItem value="critical">Critical Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Top Failed IPs */}
          {topFailedIPs.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Failed Login IPs
                </Typography>
                <Grid container spacing={2}>
                  {topFailedIPs.slice(0, 5).map((ipData, index) => (
                    <Grid item xs={12} sm={6} md={4} key={ipData.ip}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{ipData.ip}</Typography>
                        <Chip
                          label={`${ipData.count} attempts`}
                          size="small"
                          color={ipData.count >= 10 ? 'error' : ipData.count >= 5 ? 'warning' : 'default'}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Security Events Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Severity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {securityEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {new Date(event.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getActionIcon(event.action)}
                        <Chip
                          label={event.action.replace(/_/g, ' ')}
                          size="small"
                          color={getActionColor(event.action) as any}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{event.guestEmail}</TableCell>
                    <TableCell>{event.ipAddress || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDetails(event.details)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          event.action === 'ACCOUNT_LOCKED' ? 'Critical' :
                          event.action === 'PIN_LOGIN_FAILED' && (event.details?.failedAttempts || 0) >= 3 ? 'High' :
                          'Medium'
                        }
                        size="small"
                        color={
                          event.action === 'ACCOUNT_LOCKED' ? 'error' :
                          event.action === 'PIN_LOGIN_FAILED' && (event.details?.failedAttempts || 0) >= 3 ? 'error' :
                          'warning'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={currentTab} index={2}>
          {statistics && (
            <Box>
              {/* Time Range Selector */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statistics Period</InputLabel>
                    <Select
                      value={daysFilter}
                      label="Statistics Period"
                      onChange={(e) => setDaysFilter(Number(e.target.value))}
                    >
                      <MenuItem value={1}>Last 24 Hours</MenuItem>
                      <MenuItem value={7}>Last 7 Days</MenuItem>
                      <MenuItem value={30}>Last 30 Days</MenuItem>
                      <MenuItem value={90}>Last 90 Days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <TimelineIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Total Events</Typography>
                      </Box>
                      <Typography variant="h4" color="primary">
                        {statistics.summary.totalEvents}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All PIN-related events
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <SuccessIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6">Success Rate</Typography>
                      </Box>
                      <Typography variant="h4" color="success.main">
                        {statistics.summary.successRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Login success rate
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <ErrorIcon color="error" sx={{ mr: 1 }} />
                        <Typography variant="h6">Locked Accounts</Typography>
                      </Box>
                      <Typography variant="h4" color="error.main">
                        {statistics.summary.accountsLocked}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Accounts locked in period
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PersonIcon color="info" sx={{ mr: 1 }} />
                        <Typography variant="h6">Active Users</Typography>
                      </Box>
                      <Typography variant="h4" color="info.main">
                        {statistics.summary.uniqueGuestsWithActivity}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Users with PIN activity
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Action Breakdown */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action Breakdown ({statistics.timeframe})
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(statistics.actionCounts).map(([action, count]) => (
                      <Grid item xs={12} sm={6} md={4} key={action}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} border={1} borderColor="divider" borderRadius={1}>
                          <Box display="flex" alignItems="center">
                            {getActionIcon(action)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {action.replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Chip
                            label={count}
                            size="small"
                            color={getActionColor(action) as any}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};